import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpService } from '../../shared/common/helper/httpService';
import { Constants } from '../../shared/common/helper/constants';
import { UserModel } from '../../shared/common/viewmodels/user.model';
import { AlertModel } from '../../shared/common/viewmodels/alert.model';
import { NoticeBoardItem } from '../../shared/common/viewmodels/notice-board-item.model';
import { KeyDescriptionViewModel } from '../../shared/common/viewmodels/keyDescriptionViewModel';
import { BusLaneModel } from '../viewmodels/bus-lane.model';
import { CustomPermitModel } from '../viewmodels/custom-permit.model';
import { UserSettingModel } from '../../shared/common/viewmodels/user-setting.model';
import { UserDeletedModel } from '../../shared/common/user-deleted.model';
import { UserCreatedViewModel } from '../../shared/common/viewmodels/user-created-viewmodel.model';
import { CreateUserViewModel } from '../../shared/common/viewmodels/create-user-viewmodel';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { AdditionalExplanationModel } from '../../shared/common/viewmodels/additionalExplanation.model';
import { AdditionalExplanationViewModel } from '../viewmodels/additionalExplanationViewModel.model';
import { LocationViewModel } from '../viewmodels/locationViewModel.model';

@Injectable()
export class AdminService {
  private adminServiceUrl = Constants.serverUrl + 'api/admin/';

  constructor(public http: HttpService) {}

  /** getResponseMasterUsers */
  getResponseMasterUsers(): Observable<any> {
    return this.http.Get(this.adminServiceUrl + 'getResponseMasterUsers');
  }

  /** create */
  create(userModel: CreateUserViewModel): Observable<any> {
    const data = JSON.stringify(userModel);
    const origin = window.location.href;
    return this.http.Post(this.adminServiceUrl + 'create', data);
  }

  /** getResponseMasterUserById */
  getResponseMasterUserById(userId: number): Observable<any> {
    return this.http.Get(this.adminServiceUrl + 'getResponseMasterUserById' + '/' + userId);
  }

  /** getUserSettingById */
  getUserSettingById(userId: number): Observable<any> {
    return this.http.Get(this.adminServiceUrl + 'getUserSettingById' + '/' + userId);
  }

  /** update */
  update(userModel: UserModel): Observable<any> {
    const data = JSON.stringify(userModel);
    return this.http.Post(this.adminServiceUrl + 'update', data);
  }

  /** updateUserSetting */
  updateUserSetting(userModel: UserSettingModel): Observable<any> {
    const data = JSON.stringify(userModel);
    return this.http.Post(this.adminServiceUrl + 'updateUserSetting', data);
  }

  /** updateAcceptedTerms */
  updateAcceptedTerms(userId: number, acceptedTerms: boolean): Observable<any> {
    const data = JSON.stringify({ id: userId, acceptedTerms });
    return this.http.Post(`${this.adminServiceUrl}updateAcceptedTerms`, data);
  }

  /** delete */
  delete(userId: number): Observable<any> {
    const data = JSON.stringify(userId);
    return this.http.Post(this.adminServiceUrl + 'delete', data);
  }

  /** resendActivationEmail */
  resendActivationEmail(userId: number, requestOrigin: string): Observable<any> {
    const data = JSON.stringify({ userId, requestOrigin });
    return this.http.Post(this.adminServiceUrl + 'resendActivationEmail', data);
  }

  /** unlockUserAccount */
  unlockUserAccount(userId: number): Observable<any> {
    const data = JSON.stringify(userId);
    return this.http.Post(this.adminServiceUrl + 'unlockUserAccount', data);
  }

  canDeleteUser(userId: number): Observable<any> {
    const data = JSON.stringify(userId);
    return this.http.Post(this.adminServiceUrl + 'candeleteuser', data);
  }

  /** getTeamLeaders */
  getTeamLeaders(): Observable<any> {
    return this.http.Get(this.adminServiceUrl + 'getTeamLeaders');
  }

  /** getValidUsernameDomains */
  getValidUsernameDomains(): Observable<any> {
    return this.http.Get(this.adminServiceUrl + 'getValidUsernameDomains');
  }

  /** getAllAlerts */
  getAllAlerts(userId: number, showExpired: boolean, actionType: number): Observable<any> {
    return this.http.Get(
      this.adminServiceUrl + 'getAllAlerts' + '/' + userId + '/' + showExpired + '/' + actionType
    );
  }

  /** find alerts by search string */
  findAlertsBySearchString(
    searchString: string,
    showExpired: boolean,
    actionType: number
  ): Observable<any> {
    return this.http.Get(
      this.adminServiceUrl +
        'findAlertsBySearchString' +
        '/' +
        searchString +
        '/' +
        showExpired +
        '/' +
        actionType
    );
  }

  /** get alert by id */
  getAlertById(alertId: number): Observable<any> {
    return this.http.Get(this.adminServiceUrl + 'getAlertById' + '/' + alertId);
  }

  /** create alert*/
  createAlert(alertModel: AlertModel): Observable<any> {
    const data = JSON.stringify(alertModel);
    return this.http.Post(this.adminServiceUrl + 'createAlert', data);
  }

  /** update alert*/
  updateAlert(alertModel: AlertModel): Observable<any> {
    const data = JSON.stringify(alertModel);
    return this.http.Post(this.adminServiceUrl + 'updateAlert', data);
  }

  /** delete alert*/
  deleteAlert(alertId: number): Observable<any> {
    const data = JSON.stringify(alertId);
    return this.http.Post(this.adminServiceUrl + 'deleteAlert', data);
  }

  /**  get alert reasons by alert id */
  getAlertReasons(alertId: number): Observable<any> {
    return this.http.Get(this.adminServiceUrl + 'getAlertReasons' + '/' + alertId);
  }

  /**  get all reasons */
  getAllReasons(): Observable<any> {
    return this.http.Get(this.adminServiceUrl + 'getAllReasons');
  }

  /**  get Top Level Nodes Reasons */
  getTopLevelNodesReasons(): Observable<any> {
    return this.http.Get(this.adminServiceUrl + 'getTopLevelNodesReasons');
  }

  /** get alert documents by alert id */
  getAlertDocuments(alertId: number): Observable<any> {
    return this.http.Get(this.adminServiceUrl + 'getAlertDocuments' + '/' + alertId);
  }

  /** get all documents */
  getAllDocuments(): Observable<any> {
    return this.http.Get(this.adminServiceUrl + 'getAllDocuments');
  }

  /** get Top Level Nodes Documents */
  getTopLevelNodesDocuments(): Observable<any> {
    return this.http.Get(this.adminServiceUrl + 'getTopLevelNodesDocuments');
  }

  /** get alert policies by alert id */
  getAlertPolicies(alertId: number): Observable<any> {
    return this.http.Get(this.adminServiceUrl + 'getAlertPolicies' + '/' + alertId);
  }

  /** get letter cases alert */
  getLetterCasesAlert(): Observable<any> {
    return this.http.Get(this.adminServiceUrl + 'getLetterCasesAlert');
  }

  /** get alert policies */
  getAlertPoliciesRawObject(): Observable<any> {
    return this.http.Get(this.adminServiceUrl + 'getAlertPoliciesRawObject');
  }

  /** get policies categories */
  getPoliciesRoot(): Observable<any> {
    return this.http.Get(this.adminServiceUrl + 'getPoliciesRoot');
  }

  /** get all policies */
  getAllPolicies(): Observable<any> {
    return this.http.Get(this.adminServiceUrl + 'getAllPolicies');
  }

  /** save alert policies */
  saveAlertPolicies(policiesIds: any[], alertId: number): Observable<any> {
    const data = JSON.stringify(policiesIds);
    return this.http.Post(this.adminServiceUrl + 'saveAlertPolicies' + '/' + alertId, data);
  }

  /** save council image */
  saveCouncilImage(mode: string, code: string): Observable<any> {
    const object = { mode, code };
    const data = JSON.stringify(object);
    return this.http.Post(this.adminServiceUrl + 'saveCouncilImage', data);
  }

  /** get default signatures */
  getDefaultSignatures(): Observable<any> {
    return this.http.Get(this.adminServiceUrl + 'getDefaultSignatures');
  }

  /** save signature */
  saveSignature(tooltip: string, image: string): Observable<any> {
    const object = { tooltip, image };
    const data = JSON.stringify(object);
    return this.http.Post(this.adminServiceUrl + 'saveDefaultSignature', data);
  }

  /** delete signature */
  deleteSignature(signatureId: number): Observable<any> {
    return this.http.Get(this.adminServiceUrl + 'deleteSignature' + '/' + signatureId);
  }

  /** save policies */
  savePolicies(policies: KeyDescriptionViewModel[]): Observable<any> {
    const data = JSON.stringify(policies);
    return this.http.Post(this.adminServiceUrl + 'savePolicies', data);
  }

  /** get all bus lanes */
  getBusLanes(): Observable<any> {
    return this.http.Get(this.adminServiceUrl + 'getBusLanes');
  }

  /** add new bus lane */
  addBusLane(busLane: BusLaneModel): Observable<any> {
    const data = JSON.stringify(busLane);
    return this.http.Post(this.adminServiceUrl + 'addBusLane', data);
  }

  /** update bus lane */
  updateBusLane(busLane: BusLaneModel): Observable<any> {
    const data = JSON.stringify(busLane);
    return this.http.Post(this.adminServiceUrl + 'updateBusLane', data);
  }

  /** delete bus lane */
  deleteBusLane(busLaneId: number): Observable<any> {
    return this.http.Get(this.adminServiceUrl + 'deleteBusLane/' + busLaneId);
  }

  /** get custom permits */
  getCustomPermits(): Observable<any> {
    return this.http.Get(this.adminServiceUrl + 'getCustomPermits');
  }

  /** add new custom permit */
  addCustomPermit(customPermit: CustomPermitModel): Observable<any> {
    const data = JSON.stringify(customPermit);
    return this.http.Post(this.adminServiceUrl + 'addCustomPermit', data);
  }

  /** update custom permit */
  updateCustomPermit(customPermit: CustomPermitModel, permitOldName: string): Observable<any> {
    const data = JSON.stringify(customPermit);
    // console.log(data);
    // return;
    return this.http.Post(this.adminServiceUrl + 'updateCustomPermit/' + permitOldName, data);
  }

  /** delete custom permit */
  deleteCustomPermit(customPermitId: number, permitOldName: string): Observable<any> {
    const data = JSON.stringify(customPermitId);
    return this.http.Post(this.adminServiceUrl + 'deleteCustomPermit/' + permitOldName, data);
  }

  /** get system information */
  getSystemInformation(): Observable<any> {
    return this.http.Get(this.adminServiceUrl + 'getSystemInformation');
  }

  // /** get app setting information */
  // getAppSettingInformation(): Observable<any> {
  //   return this.http.Get(this.adminServiceUrl + 'getAppSettingInformation');
  // }

  /** get all offences */
  getOffences(): Observable<any> {
    return this.http.Get(this.adminServiceUrl + 'getOffences');
  }

  /** switch debug console on and off */
  // switchDebugConsole(newValue: boolean): Observable<any> {
  //   const data = JSON.stringify(newValue);
  //   return this.http.Post(this.adminServiceUrl + 'switchDebugConsole', data);
  // }

  /** setLocalOffences */
  setLocalOffences(offences: Array<any>): void {
    localStorage.setItem('rm_offences', JSON.stringify(offences));
  }

  /** getLocalOffences */
  getLocalOffences(): Array<any> {
    return localStorage.getItem('rm_offences') !== null
      ? JSON.parse(localStorage.getItem('rm_offences'))
      : [];
  }

  /** getNoticeBoardItems **/
  getNoticeBoardItems(userId: number): Observable<any> {
    return this.http.Get(this.adminServiceUrl + 'getNoticeBoardItems' + '/' + userId);
  }

  getTextResourceForName(name: string): Observable<any> {
    return this.http.Get(this.adminServiceUrl + 'getTextResourceForName' + '/' + name);
  }

  // Additional Explanation CRUD functionality for admin screens START

  addAdditionalExplanation(explanation: AdditionalExplanationViewModel): Observable<any> {
    const data = JSON.stringify(explanation);
    return this.http.Post(this.adminServiceUrl + 'addAdditionalExplanation', data);
  }

  /**  get all additional explanations */
  getAdditionalExplanations(): Observable<any> {
    return this.http.Get(this.adminServiceUrl + 'getAdditionalExplanations');
  }

  getAdditionalExplanationById(explanationId: number): Observable<any> {
    return this.http.Get(this.adminServiceUrl + 'getAdditionalExplanationById/' + explanationId);
  }

  deleteExplanation(explanationId: number): Observable<any> {
    return this.http.Get(this.adminServiceUrl + 'deleteAdditionalExplanation/' + explanationId);
  }

  updateExplanation(explanation: AdditionalExplanationModel): Observable<any> {
    const data = JSON.stringify(explanation);
    return this.http.Post(this.adminServiceUrl + 'updateAdditionalExplanation', data);
  }

  getLocations(): Observable<any> {
    return this.http.Get(this.adminServiceUrl + 'getLocations');
  }

  // Additional Explanation CRUD functionality for admin screens END

  // START Additional Explanation display in questions panel and select for letter functionality

  getAdditionalExplanationsForPcn(pcnNumber: string): Observable<any> {
    return this.http.Get(this.adminServiceUrl + 'getAdditionalExplanationForPcn/' + pcnNumber);
  }

  // getSelectedAdditionalExplanations(pcnNumber: string): Observable<any> {
  //   return this.http.Get(this.adminServiceUrl + 'getSelectedAdditionalExplanations/' + pcnNumber);
  // }

  // addSelectedAdditionalExplanations(
  //   additionalExplanationId: number,
  //   pcnNumber: string
  // ): Observable<any> {
  //   return this.http.Get(
  //     this.adminServiceUrl +
  //       'addSelectedAdditionalExplanations/' +
  //       additionalExplanationId +
  //       '/' +
  //       pcnNumber
  //   );
  // }

  // END Additional Explanation display in questions panel and select for letter functionality

  getLocationById(locationId: number): Observable<any> {
    return this.http.Get(this.adminServiceUrl + 'getLocationById/' + locationId);
  }

  addLocation(location: LocationViewModel): Observable<any> {
    const data = JSON.stringify(location);
    return this.http.Post(this.adminServiceUrl + 'addLocation', data);
  }

  deleteLocation(locationId: number): Observable<any> {
    return this.http.Get(this.adminServiceUrl + 'deleteLocation/' + locationId);
  }

  updateLocation(location: LocationViewModel): Observable<any> {
    const data = JSON.stringify(location);
    return this.http.Post(this.adminServiceUrl + 'updateLocation', data);
  }

  getLocationTypes(): Observable<any> {
    return this.http.Get(this.adminServiceUrl + 'getLocationTypes');
  }
}
